import React, { useEffect, useState } from 'react';
import { IoDownloadOutline } from 'react-icons/io5';
import { fetchOpenDataExample } from '../../services/openDataApi';
import type { OpenDataExample, OpenDataset } from '../../services/openDataApi';
import type { CatalogEntry } from '../../config/catalog';

const DownloadIcon = IoDownloadOutline as any;

const fmt = (n: number) => n.toLocaleString('en-GB');

/** A link target is used only if it is https. */
const safeHttps = (url: string | undefined): string | null => (url && url.startsWith('https://') ? url : null);

interface Props {
  entry: CatalogEntry;
}

/**
 * The example on an open data card: national total and year, cell count, the top 5 cells in a
 * disclosure, a Download CSV link and the credit line. When the API is not configured or a call
 * fails: "Example data unavailable" above the usual "API coming", and no download button.
 */
const OpenDataExampleBlock: React.FC<Props> = ({ entry }) => {
  const [example, setExample] = useState<OpenDataExample | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchOpenDataExample(entry.id as OpenDataset)
      .then((e) => {
        if (!cancelled) setExample(e);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [entry.id]);

  if (failed) {
    return (
      <div className="space-y-1">
        <p className="text-sm text-ink-faint">Example data unavailable</p>
        <div className="text-sm font-semibold text-ink-faint">API coming</div>
      </div>
    );
  }
  if (!example) return <div className="text-sm font-semibold text-ink-faint">API coming</div>;

  const creditUrl = safeHttps(example.source.url) ?? safeHttps(entry.source?.url);
  const credit = `© ${example.source.name}, ${example.source.licence}`;

  return (
    <div className="space-y-3 text-sm">
      <p className="text-ink-muted">
        <span className="font-semibold text-ink">{fmt(example.total)}</span> {example.measure} in {example.year} ·{' '}
        {fmt(example.rowCount)} cells
      </p>
      <details>
        <summary className="cursor-pointer text-xs font-semibold text-brand-600">Example data</summary>
        <table className="mt-2 w-full text-xs">
          <caption className="sr-only">Top 5 cells by {example.measure}</caption>
          <thead>
            <tr className="text-left text-ink-faint">
              <th scope="col" className="pr-2 font-normal">Lon</th>
              <th scope="col" className="pr-2 font-normal">Lat</th>
              <th scope="col" className="text-right font-normal">{example.measure}</th>
            </tr>
          </thead>
          <tbody className="font-mono text-ink-muted">
            {example.rows.map((r) => (
              <tr key={r.ssbid}>
                <td className="pr-2">{r.lon.toFixed(3)}</td>
                <td className="pr-2">{r.lat.toFixed(3)}</td>
                <td className="text-right">{fmt(r.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <a href={example.downloadUrl} download className="btn btn-outline btn-sm">
          <DownloadIcon className="h-4 w-4" />
          Download CSV
        </a>
        <span className="text-xs text-ink-faint">
          {creditUrl ? (
            <a href={creditUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
              {credit}
            </a>
          ) : (
            credit
          )}
        </span>
      </div>
    </div>
  );
};

export default OpenDataExampleBlock;
